import React, { useEffect, useRef, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import { clusterClick, createClusterElement } from "./helpers";
import { isClusterPointFeature } from "./guards";
import { ActivityProperties, MarkersMap } from "./types";
import { SOURCE_ACTIVITIES_NAME } from "./constants";
import { useActivity } from "../hooks";

interface ClusterMarkersProps {
   map: mapboxgl.Map;
}

const ClusterMarkers: React.FC<ClusterMarkersProps> = ({ map }) => {
   const markers = useRef<MarkersMap>({});
   const markersOnScreen = useRef<MarkersMap>({});
   const { userPictureCache, activities } = useActivity();

   useEffect(() => {
      for(const marker of Object.values(markers.current)) {
         marker.remove();
      }
      markers.current = {};
   }, [activities])

   const updateMarkers = useCallback(() => {
      const newMarkers: MarkersMap = {};
      const features = map.querySourceFeatures(SOURCE_ACTIVITIES_NAME);
      const source = map.getSource(
         SOURCE_ACTIVITIES_NAME
      ) as mapboxgl.GeoJSONSource;

      for (const feature of features) {
         if (!isClusterPointFeature(feature) || !feature?.properties?.cluster)
            continue;

         const [longitude, latitude] = feature.geometry.coordinates;
         const { cluster_id, point_count } = feature.properties;
         const lngLat = new mapboxgl.LngLat(longitude, latitude);

         let marker = markers.current[cluster_id];

         if (!marker) {
            const el = document.createElement("div");
            el.addEventListener("click", (e) => {
               e.stopPropagation();
               clusterClick(map, source, cluster_id, lngLat);
            });

            marker = new mapboxgl.Marker({
               element: el,
            }).setLngLat(lngLat);

            source?.getClusterLeaves(
               cluster_id,
               point_count,
               0,
               (
                  err,
                  features: GeoJSON.Feature<GeoJSON.Point, ActivityProperties>[]
               ) => {
                  if (!err) {
                     createClusterElement(el, features, userPictureCache);
                  }
               }
            );
         }

         newMarkers[cluster_id] = marker;
         if (!markersOnScreen.current[cluster_id] || !markers.current[cluster_id]) {
            marker.addTo(map);
         }
         markers.current[cluster_id] = marker;
      }

      for (const id in markersOnScreen.current) {
         if (!newMarkers[id]) {
            markersOnScreen.current[id].remove();
         }
      }
      markersOnScreen.current = newMarkers;
   }, [map]);

   useEffect(() => {
      const renderListener = () => {
         if (!map.isSourceLoaded(SOURCE_ACTIVITIES_NAME)) return;
         updateMarkers();
      };

      map.on("render", renderListener);

      return () => {
         map.off("render", renderListener);
      };
   }, [map, updateMarkers]);

   return null;
};

export default ClusterMarkers;
